import { apiErrorMessage } from '@agos/api-client'
import {
  getGetSuperAdminInvoicesQueryKey,
  useGetSuperAdminFranchises,
  useGetSuperAdminInvoices,
  usePatchSuperAdminInvoicesId,
  usePostSuperAdminBillingRun,
  usePostSuperAdminInvoices,
  usePostSuperAdminInvoicesIdResend,
  useGetSuperAdminPlatformBilling,
  getSuperAdminInvoicesIdPdf,
} from '@agos/api-client/platform'
import { T, formatCents, formatCount, tt, useModalFocus } from '@agos/shared'
import { useQueryClient } from '@tanstack/react-query'
import { useMemo, useRef, useState } from 'react'
import {
  PageHeader,
  DateRangePicker,
  FilterSelect,
  EmptyState,
  ReadFailed,
  SearchInput,
  Spinner,
  StatCard,
  StatusBadge,
} from '@agos/ui'
import { AlertTriangle, FileText } from 'lucide-react'

import {
  billingPeriodProblem,
  billingRunConfirmation,
  billingRunReport,
  billingTotals,
  chaseOrder,
  FIRST_BILLABLE_YEAR,
  MONTHS,
  daysOverdue,
  defaultBillingPeriod,
  deliveryNote,
  deliveryProblem,
  emptyManualInvoice,
  emptyManualLine,
  DEFAULT_INVOICE_SORT,
  EMPTY_INVOICE_FILTER,
  filterAndSortInvoices,
  invoiceFilterIsActive,
  invoiceFilterNote,
  invoicesOf,
  nextInvoiceSort,
  type InvoiceFilter,
  type InvoiceSort,
  type InvoiceSortKey,
  isOverdue,
  manualInvoiceBody,
  manualInvoiceConfirmation,
  manualInvoiceProblem,
  manualLineTotalCents,
  manualTotalsCents,
  markPaidBody,
  pdfFilename,
  periodNote,
  mayMarkPaid,
  mayResend,
  overdueNote,
  resendConfirmation,
  effectiveStatus,
  invoiceDatesNote,
  invoiceEditBody,
  derivedTotals,
  invoiceEditDraft,
  totalDisagreesNote,
  SETTABLE_STATUSES,
  invoiceEditProblems,
  invoiceStatusColor,
  invoiceStatusLabel,
  unsentNote,
  wasSent,
  type ManualInvoiceDraft,
  type InvoiceEditDraft,
  type InvoiceStatus,
  type PlatformInvoice,
} from './invoices'
import { platformBillingOf } from './platform-billing'

/**
 * The platform's invoices to its franchises.
 *
 * **FB-S03.** Legacy's overdue tile counts `status === "overdue"`, a status nothing ever assigns, so it
 * reads 0 in neutral grey while every invoice on the platform is unpaid and past due. Here it is
 * derived from `paidAt` and `dueAt`, and the list opens on the ones somebody needs to chase.
 */
export function BillingPage() {
  const { data, isLoading, isError } = useGetSuperAdminInvoices()
  const franchises = useGetSuperAdminFranchises()
  const invoices = useMemo(() => invoicesOf(data), [data])

  /**
   * §3.84 - narrowing the list, which this screen could not do at all.
   *
   * It drew `invoices.map(...)` and nothing else, where legacy's page has a status filter, a search
   * box, date bounds and five sortable columns. **Client-side, because the endpoint takes no
   * window**: `/super-admin/billing/invoices` declares no `from`/`to`, unlike the three other Tower
   * reports, so the rows arrive whole and the screen narrows them - which is what legacy does too.
   *
   * **A sort control rather than clickable column headers.** This is a card list, not a table, so
   * there is no `<th>` to click; porting the *feature* means offering the five orders legacy offers,
   * not reproducing its markup.
   */
  const [filter, setFilter] = useState<InvoiceFilter>(EMPTY_INVOICE_FILTER)
  const [sort, setSort] = useState<InvoiceSort>(DEFAULT_INVOICE_SORT)
  const shown = useMemo(
    () => filterAndSortInvoices(invoices, filter, sort),
    [invoices, filter, sort],
  )
  // Billing addresses by slug, so an invoice can say whether it could have been delivered at all.
  const billingEmails = useMemo(() => {
    const rows = Array.isArray((franchises.data as { franchises?: unknown } | undefined)?.franchises)
      ? (franchises.data as { franchises: Record<string, unknown>[] }).franchises
      : []
    return new Map(
      rows.map((row) => [
        typeof row.slug === 'string' ? row.slug : '',
        typeof row.billingEmail === 'string' ? row.billingEmail : null,
      ]),
    )
  }, [franchises.data])
  // Read once per render, after the memo, so the compiler has nothing non-deterministic inside it.
  const now = new Date()
  const totals = billingTotals(invoices, now)
  const chasing = chaseOrder(invoices, now)

  // ─── §3.8 - the three writes that end a chase ─────────────────────────────
  //
  // FB-505: *"the platform's own billing is a report… six unpaid invoices that cannot be resent
  // from here."* And `PlatformInvoiceService.update` has a comment about *"the button on the
  // screen"* - which did not exist until now.
  const queryClient = useQueryClient()
  const [asking, setAsking] = useState<
    { kind: 'resend'; invoice: PlatformInvoice } | { kind: 'run' } | { kind: 'raise' } | null
  >(null)
  const [runReport, setRunReport] = useState('')
  /**
   * The invoice being corrected, with the form's stored baseline beside it - §3.97.
   *
   * The baseline is kept rather than re-derived on save: `invoiceEditBody` sends only what moved, and
   * comparing against a freshly derived draft would compare the form with itself.
   */
  const [editing, setEditing] = useState<
    { invoice: PlatformInvoice; draft: InvoiceEditDraft } | null
  >(null)
  // §3.19, the owner's #16: *"Raise yes, delete never."* The draft holds text because these are
  // inputs; every figure that ends up on the document is derived, here or on the server.
  const [draft, setDraft] = useState<ManualInvoiceDraft>(emptyManualInvoice())
  const [raiseReport, setRaiseReport] = useState('')
  const supplier = platformBillingOf(useGetSuperAdminPlatformBilling().data)
  const franchiseOptions = useMemo(() => {
    const rows = Array.isArray((franchises.data as { franchises?: unknown } | undefined)?.franchises)
      ? (franchises.data as { franchises: Record<string, unknown>[] }).franchises
      : []
    return rows.flatMap((row) =>
      typeof row.id === 'number' && typeof row.name === 'string'
        ? [{ id: row.id, name: row.name }]
        : [],
    )
  }, [franchises.data])
  const draftProblem = manualInvoiceProblem(draft)
  const draftTotals = manualTotalsCents(draft, supplier.defaultVatRatePct)
  const draftFranchiseName =
    franchiseOptions.find((option) => option.id === draft.franchiseId)?.name ?? ''
  /**
   * The period the run will bill - §3.183, FB-957.
   *
   * **Held in state because it is now a choice.** It was `defaultBillingPeriod(now)` computed every
   * render and sent as the only period this screen could ever ask for; the initial value is that same
   * period, so nothing about the ordinary press changed.
   *
   * `useState`'s initialiser runs once, which is what makes `now` safe to read inside it: a screen left
   * open across midnight on the first of a month keeps the period the operator is looking at rather than
   * silently moving it under the confirmation dialog.
   */
  const [runPeriod, setRunPeriod] = useState(() => defaultBillingPeriod(new Date()))
  const runPeriodProblem = billingPeriodProblem(runPeriod.year, runPeriod.month, now)

  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: getGetSuperAdminInvoicesQueryKey() })

  /**
   * What the server refused, said out loud - `SAAS.md` §3.35.
   *
   * **Four mutations, no `onError`.** These mark a tax document paid, resend it and start the billing
   * run. A run the server refuses - §3.18's double-billing guard is the likeliest - looked exactly
   * like a run that did nothing.
   *
   * `apiErrorMessage` is the shared reader - it takes `error` off the backend's `{error, code}`
   * envelope. The fallback covers a network drop, which carries no envelope to read.
   */
  const [refused, setRefused] = useState<string | null>(null)
  const refusedWith = (error: unknown) => {
    setRefused(apiErrorMessage(error, tt('Zmenu sa nepodarilo uložiť - server neodpovedal.')))
  }

  // A refusal is cleared the moment anything succeeds - a banner left over from an earlier attempt
  // is a screen stating something that is no longer true.
  const succeeded = () => {
    setRefused(null)
    refresh()
  }

  /**
   * Open or keep the document - §3.39.
   *
   * **The Tower could mark an invoice paid and re-send it without anybody being able to look at it.**
   * `GET .../invoices/{id}/pdf` renders from the row each time and had no caller; legacy's own
   * `Billing.tsx` has two, a download and an open-in-a-tab, and both are here for the same reason
   * they are there: *now* and *keep* are different questions.
   *
   * Through the generated fetcher rather than `fetch` - `customFetch` infers `blob` from the
   * `application/pdf` content type and already attaches the bearer, so there is no new transport and
   * no exception to the lint rule.
   *
   * **The object URL is revoked in both branches.** Legacy revokes it for the download and leaks it
   * for the view; a blob URL pins the whole PDF for the life of the document, and this is a list.
   */
  const withThePdf = async (
    invoice: PlatformInvoice,
    handOff: (url: string) => void,
  ): Promise<void> => {
    try {
      const body = (await getSuperAdminInvoicesIdPdf(invoice.id, {
        responseType: 'blob',
      })) as unknown as Blob
      const url = URL.createObjectURL(body)
      try {
        handOff(url)
      } finally {
        // Revoked on the next tick: an `<a download>` click and a `window.open` both need the URL to
        // survive the current one, and neither needs it after.
        window.setTimeout(() => URL.revokeObjectURL(url), 0)
      }
    } catch (failure) {
      // Named, because a blank tab is the one outcome that tells the operator nothing. The renderer
      // throws on a row it cannot render - a missing supplier block is the likeliest, §9.153.
      setRefused(
        apiErrorMessage(
          failure,
          `${tt('Faktúru sa nepodarilo otvoriť')}: ${invoice.invoiceNumber || invoice.id}`,
        ),
      )
    }
  }

  const openPdf = (invoice: PlatformInvoice) =>
    void withThePdf(invoice, (url) => window.open(url, '_blank'))

  const downloadPdf = (invoice: PlatformInvoice) =>
    void withThePdf(invoice, (url) => {
      const link = document.createElement('a')
      link.href = url
      link.download = pdfFilename(invoice)
      link.click()
    })

  const markPaid = usePatchSuperAdminInvoicesId({ mutation: { onSuccess: succeeded, onError: refusedWith } })
  /* §3.97 - the same endpoint the paid button uses, with the five correctable fields. Its own
   * mutation so a failing correction cannot read as a failing *Zaplatená*. */
  const saveEdit = usePatchSuperAdminInvoicesId({
    mutation: {
      onSuccess: () => {
        setEditing(null)
        succeeded()
      },
      onError: refusedWith,
    },
  })
  const resend = usePostSuperAdminInvoicesIdResend({
    mutation: {
      onSuccess: () => {
        setAsking(null)
        succeeded()
      },
      onError: refusedWith,
    },
  })
  const raise = usePostSuperAdminInvoices({
    mutation: {
      onSuccess: () => {
        setAsking(null)
        // The number is the server's, so the report reads it back rather than predicting it.
        setRaiseReport(tt('Faktúra bola vystavená.'))
        setDraft(emptyManualInvoice())
        succeeded()
      },
      onError: refusedWith,
    },
  })
  const runBilling = usePostSuperAdminBillingRun({
    mutation: {
      onSuccess: (answer) => {
        setAsking(null)
        setRunReport(billingRunReport(answer))
        succeeded()
      },
      onError: refusedWith,
    },
  })

  /* §9.489, `TODO.md` 3f - both of this screen's overlays said `aria-modal="true"` and confined nothing.
     **A pair of refs and a pair of handlers, not one of each**, which is the hook's own design rather than a
     preference: it returns an element-level handler *"because two open dialogs - a confirmation over an
     editor - would otherwise both answer one Tab"*, and this screen is exactly that pair. Each is guarded on
     the writes it can start, so Escape does nothing while one is in flight (§9.480). */
  const editPanelRef = useRef<HTMLDivElement>(null)
  const { onKeyDown: editKeyDown } = useModalFocus(
    editing !== null,
    editPanelRef,
    saveEdit.isPending ? null : () => setEditing(null),
  )
  const confirmRef = useRef<HTMLDivElement>(null)
  const { onKeyDown: confirmKeyDown } = useModalFocus(
    asking !== null,
    confirmRef,
    resend.isPending || runBilling.isPending || raise.isPending ? null : () => setAsking(null),
  )

  return (
    <main className="space-y-3" data-billing>
      {/* §3.78 - the title block every other screen in the product opens with. This page had
        * none at all: you landed on it and nothing on the screen said what it was. */}
      <PageHeader title={tt('Fakturácia')} description={tt('Faktúry vystavené franšízam a stav ich úhrady.')} />
      {/* §3.82 - a screen that cannot load its data must not render its empty state.
        * Discarding `isLoading` and `isError` meant *"Žiadne faktúry"* appeared while
        * the request was in flight and again when it failed - so a broken load read as a quiet
        * period, which is §3.52's argument applied to the six screens it did not reach. */}
      {isError && (
        <ReadFailed marker="invoices-error"><T>Faktúry sa nepodarilo načítať.</T></ReadFailed>
      )}
      {isLoading && (
        <div className="py-10 flex justify-center" data-invoices-loading>
          <Spinner />
        </div>
      )}


      {refused !== null && (
        <p
          className="rounded-2xl border border-red-500/40 bg-red-500/5 p-3 text-xs text-red-500"
          data-billing-refused
          role="alert"
        >
          {refused}
        </p>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {/* §3.183, FB-957 - **this month's billed revenue, which legacy's screen has and no AGOS screen
          * had.** The owner's report, and the sharpest half of it: a billing run's whole output is this
          * month's invoices, so after pressing *Vyfakturovať* the one thing that moved was a line of
          * text. The count is under it as legacy's is - *23 faktúr* answers *did it run for everybody*,
          * where the sum alone cannot. */}
        <div data-this-month-count={String(totals.thisMonthCount)}>
          <StatCard
            testId="this-month"
            label={tt('Tento mesiac')}
            value={formatCents(totals.thisMonthCents)}
            sub={`${formatCount(totals.thisMonthCount)} ${tt('faktúr')}`}
            icon={FileText}
            /* **Emerald only when something was billed**, which is FB-810's rule caught on this entry's own
             * new tile. Legacy's is emerald unconditionally, and on the owner's data this figure is
             * **nought** - no run since July, because production pins `AGOS_JOBS_ENABLED=false` - so a green
             * `0,00 €` would read as good news on the one card whose nought is the problem. The neutral is
             * the same `: 'primary'` the overdue card beside it uses, inverted. */
            color={totals.thisMonthCount > 0 ? 'emerald' : 'primary'}
          />
        </div>
        {/* §3.81 - the shared card. `data-overdue-count` stays on a wrapper: it is a figure a spec
          * reads off the element rather than out of the text, and `StatCard` takes no arbitrary
          * props. The colour still turns on whether anything is actually overdue. */}
        <StatCard
          testId="outstanding"
          label={tt('Nezaplatené')}
          value={formatCents(totals.outstandingCents)}
          icon={FileText}
          color="blue"
        />
        <div data-overdue-count={String(totals.overdueCount)}>
          <StatCard
            testId="overdue"
            label={tt('Po splatnosti')}
            value={formatCount(totals.overdueCount)}
            sub={overdueNote(totals) === '' ? undefined : overdueNote(totals)}
            icon={AlertTriangle}
            color={totals.overdueCount > 0 ? 'red' : 'primary'}
          />
        </div>
        {/* §3.183 - legacy's fourth tile. The list's own length rather than a figure in `billingTotals`:
          * a count derived from the rows it is printed beside cannot disagree with them, which is §3.48's
          * rule and the reason it is not asked of the server. */}
        <StatCard
          testId="issued-total"
          label={tt('Spolu vystavené')}
          value={formatCount(invoices.length)}
          sub={tt('všetky obdobia')}
          icon={FileText}
          color="primary"
        />
      </div>

      {unsentNote(invoices) !== '' && (
        <p className="text-xs text-amber-500" data-unsent-note>
          {unsentNote(invoices)}
        </p>
      )}

      {/* §3.84 - the filter bar. Above the list rather than in the header, because what it narrows
        * is the list and not the two cards above it: those are the platform's whole outstanding
        * position and must not move when somebody searches for one franchise. */}
      <div className="flex flex-wrap items-center gap-2" data-invoice-filters>
        <SearchInput
          value={filter.search}
          onChange={(search) => setFilter({ ...filter, search })}
          placeholder={tt('Faktúra, franšíza alebo VS')}
          className="w-56"
        />
        <FilterSelect
          value={filter.status}
          onChange={(status) => setFilter({ ...filter, status })}
          label={tt('Stav')}
          options={[
            { value: 'all', label: tt('Všetky stavy') },
            { value: 'draft', label: tt('Návrh') },
            { value: 'issued', label: tt('Vystavená') },
            { value: 'paid', label: tt('Zaplatená') },
            { value: 'overdue', label: tt('Po splatnosti') },
            { value: 'void', label: tt('Zrušená') },
          ]}
        />
        <DateRangePicker
          from={filter.from}
          to={filter.to}
          onFromChange={(from) => setFilter({ ...filter, from })}
          onToChange={(to) => setFilter({ ...filter, to })}
        />
        <FilterSelect
          value={sort.key}
          onChange={(key) => setSort({ ...sort, key: key as InvoiceSortKey })}
          label={tt('Zoradiť')}
          options={[
            { value: 'issuedAt', label: tt('Dátum vystavenia') },
            { value: 'dueAt', label: tt('Splatnosť') },
            { value: 'amount', label: tt('Suma') },
            { value: 'franchiseName', label: tt('Franšíza') },
            { value: 'status', label: tt('Stav') },
          ]}
        />
        <button
          type="button"
          data-invoice-sort-dir={sort.dir}
          onClick={() => setSort(nextInvoiceSort(sort, sort.key))}
          className="px-2.5 py-2 rounded-xl border border-border text-xs font-bold text-muted-foreground hover:bg-muted"
        >
          {sort.dir === 'desc' ? tt('Najnovšie') : tt('Najstaršie')}
        </button>
        <span className="text-xs text-muted-foreground" data-invoice-count>
          {invoiceFilterNote(shown.length, invoices.length)}
        </span>
        {invoiceFilterIsActive(filter) && (
          <button
            type="button"
            data-invoice-filter-clear
            onClick={() => setFilter(EMPTY_INVOICE_FILTER)}
            className="px-2.5 py-2 rounded-xl border border-border text-xs font-bold text-muted-foreground hover:bg-muted"
          >
            {tt('Zrušiť filter')}
          </button>
        )}
      </div>

      <section className="space-y-1" data-invoice-list>
        {chasing.length > 0 && (
          <p className="text-xs font-semibold" data-chase-heading>
            <T>Na vymáhanie</T>
          </p>
        )}
        {shown.map((invoice) => (
          <div
            key={invoice.id}
            className="rounded-xl border border-border p-2 text-xs"
            data-invoice={String(invoice.id)}
            data-invoice-overdue={isOverdue(invoice, now) ? 'yes' : 'no'}
          >
            <p>
              <span className="font-semibold">{invoice.invoiceNumber}</span> &middot; {invoice.franchiseName}{' '}
              &middot; <span data-invoice-amount>{formatCents(invoice.amountCents)}</span>{' '}
              {/* §3.94 - **the status, which this screen could filter and sort by and never showed.**
                * Filtering to *Zaplatená* returned a list in which nothing said which were paid, and
                * sorting by *Stav* reordered rows by a field the reader could not see. Legacy has a
                * sortable Status column with a coloured badge.
                *
                * The **effective** state, not the stored one: nothing ever assigns `overdue`, so an
                * invoice stored as `issued` past its due date reads *Po splatnosti* - otherwise the
                * filter offers an option no row could ever match. */}
              <span data-invoice-status={effectiveStatus(invoice, now)}>
                <StatusBadge
                  label={invoiceStatusLabel(effectiveStatus(invoice, now))}
                  color={invoiceStatusColor(effectiveStatus(invoice, now))}
                />
              </span>
              {/* §3.44 - the three the endpoint answered and the row never showed. The variable
                * symbol is how a Slovak franchise pays this, and the period is the first question
                * anybody asks about an invoice; §3.39's PDF carries both and opening a document to
                * read one line is worse than the line. */}
              {invoice.variableSymbol !== null && (
                <>
                  {' · '}
                  <T>VS</T>{' '}
                  <span className="font-mono" data-invoice-vs>
                    {invoice.variableSymbol}
                  </span>
                </>
              )}
              {/* §3.95 - the two dates the sort offers and the row never showed. `daysOverdue` says
                * *how late*, the badge says *late*, and this says *since when* - which is what a
                * franchise asks on the telephone, and it is invisible entirely on an invoice that is
                * not yet due. */}
              {invoiceDatesNote(invoice) !== '' && (
                <span className="text-muted-foreground" data-invoice-dates>
                  {' · '}
                  {invoiceDatesNote(invoice)}
                </span>
              )}
              {invoice.vatAmountCents > 0 && (
                <span className="text-muted-foreground" data-invoice-vat>
                  {' · '}
                  <T>DPH</T> {formatCents(invoice.vatAmountCents)}
                </span>
              )}
            </p>
            {periodNote(invoice) !== '' && (
              <p className="text-[10px] text-muted-foreground" data-invoice-period>
                {periodNote(invoice)}
              </p>
            )}
            {/* Who it actually went to, which the settings-based resend guard cannot answer. */}
            {invoice.sentTo !== null && (
              <p className="text-[10px] text-muted-foreground" data-invoice-sent-to>
                <T>Poslané na</T> {invoice.sentTo}
              </p>
            )}
            {isOverdue(invoice, now) && (
              <p className="text-[10px] text-red-500" data-invoice-days>
                <T>Po splatnosti o dní</T>: {formatCount(daysOverdue(invoice, now))}
              </p>
            )}
            {/* §3.124 - **the owner's condition on #31, finally said.** They answered *"let the total
              * be overridden"* provided *"it must say so on the invoice"*; §3.110 derived
              * `totalMatchesLines` and nothing read it, so the condition was unmet from the day it was
              * recorded as met. Only the divergence is drawn: `true` needs no badge, and `null` - no
              * lines to compare, the normal shape for a billing-run invoice - is silence rather than a
              * caveat, or the handful that matter would be buried. */}
            {totalDisagreesNote(invoice) !== '' && (
              <p className="text-[10px] text-amber-500" data-invoice-total-disagrees>
                {totalDisagreesNote(invoice)}
              </p>
            )}
            {/* §3.121 - **the reason, on the row a voided invoice is on.** The editor refuses a void
              * without a note, so this is where that note ends up being read: the question somebody
              * asks of a cancelled tax document is *why*, and opening an editor to answer it is
              * worse than a line of text. Only for `void`, because for every other state the note is
              * an optional remark rather than the thing being asserted. */}
            {invoice.status === 'void' && invoice.notes !== null && invoice.notes.trim() !== '' && (
              <p className="text-[10px] text-muted-foreground" data-invoice-void-reason>
                <T>Dôvod zrušenia</T>: {invoice.notes}
              </p>
            )}
            {!wasSent(invoice) && (
              <p className="text-[10px] text-amber-500" data-invoice-unsent>
                <T>Neodoslaná</T>
                {/* §3.8 - the attribute carries **which** problem, because the two are not the
                  * same thing. `missing` means `BillingRunner` skips the send entirely and a resend
                  * cannot work; `suspicious-domain` means the address exists and may bounce, which
                  * is worth saying and is no reason to refuse the attempt. The resend button is off
                  * for the first and on for the second, and this is what lets a test assert that
                  * distinction rather than lumping the two together. */}
                {deliveryNote(deliveryProblem(billingEmails.get(invoice.franchiseSlug) ?? null)) !== '' && (
                  <span
                    data-invoice-undeliverable={
                      deliveryProblem(billingEmails.get(invoice.franchiseSlug) ?? null) ?? ''
                    }
                  >
                    {' '}
                    - {deliveryNote(deliveryProblem(billingEmails.get(invoice.franchiseSlug) ?? null))}
                  </span>
                )}
              </p>
            )}

            {/* §3.8 - the two actions that end this invoice's chase. */}
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              <button
                type="button"
                data-invoice-open={String(invoice.id)}
                onClick={() => openPdf(invoice)}
                className="px-2 py-1 rounded-lg border border-border text-[10px] font-bold"
              >
                <T>Otvoriť</T>
              </button>
              <button
                type="button"
                data-invoice-download={String(invoice.id)}
                onClick={() => downloadPdf(invoice)}
                className="px-2 py-1 rounded-lg border border-border text-[10px] font-bold"
              >
                <T>Stiahnuť</T>
              </button>
              <button
                type="button"
                data-invoice-mark-paid={String(invoice.id)}
                disabled={!mayMarkPaid(invoice) || markPaid.isPending}
                onClick={() => markPaid.mutate({ id: invoice.id, data: markPaidBody() })}
                className="px-2 py-1 rounded-lg border border-emerald-500/40 text-emerald-500 text-[10px] font-bold disabled:opacity-40"
              >
                <T>Zaplatená</T>
              </button>
              {/* §3.97 - **AGOS had no way to correct an invoice at all.** The server takes twelve
                * fields on the PATCH and this screen sent one; legacy has an edit modal with five.
                * Those five, and none of the four owner question #31 is about. */}
              <button
                type="button"
                data-invoice-edit={String(invoice.id)}
                onClick={() => setEditing({ invoice, draft: invoiceEditDraft(invoice) })}
                className="px-2 py-1 rounded-lg border border-border text-[10px] font-bold"
              >
                <T>Upraviť</T>
              </button>
              <button
                type="button"
                data-invoice-resend={String(invoice.id)}
                disabled={
                  !mayResend(billingEmails.get(invoice.franchiseSlug) ?? null) || resend.isPending
                }
                onClick={() => setAsking({ kind: 'resend', invoice })}
                className="px-2 py-1 rounded-lg border border-border text-[10px] font-bold disabled:opacity-40"
              >
                <T>Poslať znova</T>
              </button>
            </div>
          </div>
        ))}
        {!isLoading && !isError && invoices.length === 0 && (
          <div data-no-invoices>
            <EmptyState
              icon={<FileText className="w-12 h-12" />}
              title={tt('Žiadne faktúry')}
              description={tt('Za toto obdobie nebola vystavená žiadna faktúra.')}
            />
          </div>
        )}
      </section>

      {/* §3.8 - the run that raises a month's invoices. Offerable because the controller's own note
        * says pressing it twice is safe: a franchise already invoiced for the period is skipped. */}
      <section className="rounded-2xl border border-border p-3 space-y-2" data-billing-run>
        <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          <T>Vyfakturovať obdobie</T>
        </p>
        {/* §3.183, FB-957 - **the year and the month, which this screen never offered.** It sent
          * `defaultBillingPeriod(now)` and nothing else, so the only period the Tower could invoice was
          * the previous month - and a month the scheduler missed, with production pinning
          * `AGOS_JOBS_ENABLED=false` (§10A.278), could not be billed from the product at all. The
          * endpoint has taken `{year, month}` since it was written, and legacy's page has both inputs.
          *
          * **Defaulted to the same period the button used to hard-code**, so the ordinary press - billing
          * September on the first of October - is unchanged and takes no more clicks than before. */}
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-[11px] text-muted-foreground space-y-0.5">
            <span className="block font-semibold">
              <T>Rok</T>
            </span>
            <input
              type="number"
              data-billing-run-year
              value={runPeriod.year}
              min={FIRST_BILLABLE_YEAR}
              max={now.getFullYear()}
              onChange={(event) =>
                setRunPeriod({ ...runPeriod, year: Number(event.target.value) })
              }
              className="w-20 min-h-[32px] rounded-lg border border-border bg-background px-2 text-xs"
            />
          </label>
          <label className="text-[11px] text-muted-foreground space-y-0.5">
            <span className="block font-semibold">
              <T>Mesiac</T>
            </span>
            {/* A select rather than a number input: twelve values with names is a shorter control than a
              * box that can hold 13, and it cannot produce a month the refusal has to explain. */}
            <select
              data-billing-run-month
              value={runPeriod.month}
              onChange={(event) =>
                setRunPeriod({ ...runPeriod, month: Number(event.target.value) })
              }
              className="min-h-[32px] rounded-lg border border-border bg-background px-2 text-xs"
            >
              {MONTHS.map((month) => (
                <option key={month} value={month}>
                  {String(month).padStart(2, '0')}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            data-billing-run-open
            disabled={runBilling.isPending || runPeriodProblem !== null}
            onClick={() => setAsking({ kind: 'run' })}
            className="min-h-[32px] px-3 py-1.5 rounded-lg border border-primary/40 bg-primary/10 text-primary text-xs font-semibold disabled:opacity-40"
          >
            {String(runPeriod.month).padStart(2, '0')}/{runPeriod.year}
          </button>
        </div>
        {runPeriodProblem !== null && (
          <p className="text-[11px] text-destructive" data-billing-run-problem>
            {runPeriodProblem}
          </p>
        )}
        {runReport !== '' && (
          <p className="text-[11px] text-muted-foreground" data-billing-run-report>
            {runReport}
          </p>
        )}
      </section>


      {/* §3.19 - raising one by hand. The owner said yes to this and never to deleting one. */}
      <section className="rounded-2xl border border-border p-3 space-y-2" data-raise-invoice>
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-semibold">
            <T>Vystaviť faktúru ručne</T>
          </p>
          <p className="text-[11px] text-muted-foreground">
            <T>Číslo, obdobie, DPH a súčty priradí server</T>
          </p>
        </div>

        <select
          data-raise-franchise
          value={draft.franchiseId ?? ''}
          onChange={(event) =>
            setDraft({
              ...draft,
              franchiseId: event.target.value === '' ? null : Number(event.target.value),
            })
          }
          className="w-full min-h-[36px] rounded-xl border border-border bg-background px-2 text-xs"
        >
          <option value="">{tt('Vyberte franšízu...')}</option>
          {franchiseOptions.map((option) => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </select>

        {draft.lines.map((line, index) => (
          <div key={index} className="grid grid-cols-12 gap-1" data-raise-line={String(index)}>
            <input
              data-raise-description={String(index)}
              value={line.description}
              maxLength={300}
              placeholder={tt('Popis položky')}
              onChange={(event) =>
                setDraft({
                  ...draft,
                  lines: draft.lines.map((row, rowIndex) =>
                    rowIndex === index ? { ...row, description: event.target.value } : row,
                  ),
                })
              }
              /* §3.183 - four rather than five, so the remove button's column fits inside the same
                 twelve: 4 + 2 + 2 + 2 + 1 + 1. A thirteenth column wraps the row. */
              className="col-span-4 min-h-[32px] rounded-lg border border-border bg-background px-2 text-xs"
            />
            <input
              data-raise-units={String(index)}
              value={line.units}
              maxLength={20}
              placeholder={tt('ks')}
              onChange={(event) =>
                setDraft({
                  ...draft,
                  lines: draft.lines.map((row, rowIndex) =>
                    rowIndex === index ? { ...row, units: event.target.value } : row,
                  ),
                })
              }
              className="col-span-2 min-h-[32px] rounded-lg border border-border bg-background px-2 text-xs"
            />
            <input
              data-raise-quantity={String(index)}
              value={line.quantity}
              inputMode="decimal"
              onChange={(event) =>
                setDraft({
                  ...draft,
                  lines: draft.lines.map((row, rowIndex) =>
                    rowIndex === index ? { ...row, quantity: event.target.value } : row,
                  ),
                })
              }
              className="col-span-2 min-h-[32px] rounded-lg border border-border bg-background px-2 text-xs"
            />
            <input
              data-raise-unit-price={String(index)}
              value={line.unitPrice}
              inputMode="decimal"
              placeholder={tt('cena')}
              onChange={(event) =>
                setDraft({
                  ...draft,
                  lines: draft.lines.map((row, rowIndex) =>
                    rowIndex === index ? { ...row, unitPrice: event.target.value } : row,
                  ),
                })
              }
              className="col-span-2 min-h-[32px] rounded-lg border border-border bg-background px-2 text-xs"
            />
            {/* **Read-only, and that is the point.** The server sums the lines' own totals, so a
                typed total could bill something the line does not say - §3.19. */}
            <p
              data-raise-line-total={String(index)}
              className="col-span-1 self-center text-right text-[11px] tabular-nums"
            >
              {formatCents(manualLineTotalCents(line))}
            </p>
            {/* §3.183, FB-957 - **the counterpart *Pridať položku* never had.** `manualInvoiceProblem`
                refuses a line with a blank description, so one stray click made the invoice unissuable
                and the only way out was reloading the page, which discards the franchise and every line
                already typed. Legacy has a `Minus` on each line.
                **Not on the last line**, because that press would produce *Faktúra musí mať aspoň jednu
                položku* - the same dead end one step along. Legacy allows the empty list and then
                disables its own create; refusing to reach the state is the better half of that pair. */}
            {draft.lines.length > 1 && (
              <button
                type="button"
                data-raise-remove-line={String(index)}
                aria-label={tt('Odstrániť položku')}
                title={tt('Odstrániť položku')}
                onClick={() =>
                  setDraft({
                    ...draft,
                    lines: draft.lines.filter((_, rowIndex) => rowIndex !== index),
                  })
                }
                className="col-span-1 self-center justify-self-end rounded-lg border border-border px-1.5 py-0.5 text-[11px] font-bold text-muted-foreground hover:bg-muted"
              >
                &times;
              </button>
            )}
          </div>
        ))}

        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            data-raise-add-line
            onClick={() => setDraft({ ...draft, lines: [...draft.lines, emptyManualLine()] })}
            className="px-2 py-1 rounded-lg border border-border text-[11px] font-semibold"
          >
            <T>Pridať položku</T>
          </button>
          <p className="text-[11px] text-muted-foreground" data-raise-totals>
            {formatCents(draftTotals.subtotalCents)} + {formatCount(supplier.defaultVatRatePct)} %{' '}
            <T>DPH</T> = <span className="font-semibold">{formatCents(draftTotals.totalCents)}</span>
          </p>
        </div>

        {draftProblem !== null && (
          <p className="text-[11px] text-destructive" data-raise-problem>
            {draftProblem}
          </p>
        )}
        <button
          type="button"
          data-raise-open
          disabled={draftProblem !== null || raise.isPending}
          onClick={() => setAsking({ kind: 'raise' })}
          className="w-full min-h-[36px] rounded-xl border border-primary/40 bg-primary/10 text-primary text-xs font-bold disabled:opacity-40"
        >
          <T>Vystaviť faktúru</T>
        </button>
        {raiseReport !== '' && (
          <p className="text-[11px] text-muted-foreground" data-raise-report>
            {raiseReport}
          </p>
        )}
      </section>

      {/* §3.97 - the edit form. A dialog rather than an inline row, because these five fields are a
        * correction to a tax document and that is a deliberate act, not a stray keystroke in a list.
        *
        * **`subtotal`, `vatAmount`, `amount` and `lineItems` are absent on purpose**: the server takes
        * all four and owner question #31 is open on exactly whether an amount may be corrected without
        * its lines. Legacy's own modal has no money field either, which is what makes these five a
        * port rather than a decision. */}
      {editing !== null && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={tt('Upraviť faktúru')}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          data-invoice-edit-dialog={String(editing.invoice.id)}
        >
          <div
            ref={editPanelRef}
            onKeyDown={editKeyDown}
            className="w-full max-w-md rounded-2xl border border-border bg-card p-4 space-y-3"
          >
            <p className="text-sm font-bold">
              <T>Upraviť faktúru</T> {editing.invoice.invoiceNumber}
            </p>
            <div className="space-y-2">
              {(
                [
                  ['invoiceNumber', tt('Číslo faktúry'), 'text'],
                  ['variableSymbol', tt('Variabilný symbol'), 'text'],
                  ['deliveryDate', tt('Dátum dodania'), 'date'],
                  ['dueAt', tt('Dátum splatnosti'), 'date'],
                  ['vatRatePct', tt('DPH %'), 'number'],
                ] as const
              ).map(([field, label, kind]) => (
                <label key={field} className="block text-[11px] text-muted-foreground space-y-0.5">
                  <span className="block font-semibold">{label}</span>
                  <input
                    type={kind}
                    data-invoice-edit-field={field}
                    value={editing.draft[field]}
                    onChange={(event) =>
                      setEditing((current) =>
                        current === null
                          ? null
                          : { ...current, draft: { ...current.draft, [field]: event.target.value } },
                      )
                    }
                    className="w-full min-h-[32px] rounded-lg border border-border bg-background px-2 text-xs"
                  />
                </label>
              ))}
            </div>
            {/* §3.126 - **the net, and the two figures it comes to, live.**
              *
              * Owner answer #31 - *"leave it - an accountant needs it"* - and until now no control
              * existed. One typed figure rather than three: with `vatRatePct` already on this form,
              * three inputs would let **four** figures disagree, and a tax document whose VAT is not
              * its rate applied to its base is not defensible. Deriving two leaves exactly one thing
              * that can diverge - the base against the lines - which §3.110 computes and §3.124 prints
              * on the row.
              *
              * **The two derived figures are under the field rather than elsewhere**: the whole risk
              * of this control is somebody typing a gross figure into a net box, and these are how
              * they notice before saving. */}
            <label className="block text-[11px] text-muted-foreground space-y-0.5">
              <span className="block font-semibold">
                <T>Základ (bez DPH)</T>
              </span>
              <input
                data-invoice-edit-subtotal
                inputMode="decimal"
                value={editing.draft.subtotal}
                onChange={(event) =>
                  setEditing((current) =>
                    current === null
                      ? null
                      : { ...current, draft: { ...current.draft, subtotal: event.target.value } },
                  )
                }
                className="w-full min-h-[32px] rounded-lg border border-border bg-background px-2 text-xs"
              />
              {(() => {
                const derived = derivedTotals(editing.draft.subtotal, editing.draft.vatRatePct)
                return derived === null ? null : (
                  <span className="block text-[10px]" data-invoice-edit-derived>
                    <T>DPH</T> {formatCents(derived.vatAmountCents)} &middot;{' '}
                    <T>Celkom</T> {formatCents(derived.amountCents)}
                  </span>
                )
              })()}
            </label>
            {/* §3.121 - the state. The filter has offered five since §3.84 and the editor could set
                **one**: `draft` and `void` were reachable by nothing at all. `overdue` is deliberately
                not on this list - AGOS derives it from `paidAt`/`dueAt`, and FB-S03 is the entry that
                took the stored one out of the tile because *"nothing ever assigns"* it. */}
            <label className="block text-[11px] text-muted-foreground space-y-0.5">
              <span className="block font-semibold">
                <T>Stav</T>
              </span>
              <select
                data-invoice-edit-status
                value={editing.draft.status}
                onChange={(event) =>
                  setEditing((current) =>
                    current === null
                      ? null
                      : {
                          ...current,
                          draft: { ...current.draft, status: event.target.value as InvoiceStatus },
                        },
                  )
                }
                className="w-full min-h-[32px] rounded-lg border border-border bg-background px-2 text-xs"
              >
                {SETTABLE_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {invoiceStatusLabel(status)}
                  </option>
                ))}
              </select>
            </label>
            {/* §3.121 - the note, which the server has answered on every row since the beginning and
                nothing could read or write. It is the only field that can hold *why* an invoice was
                voided, which is why voiding refuses without one. */}
            <label className="block text-[11px] text-muted-foreground space-y-0.5">
              <span className="block font-semibold">
                <T>Poznámka</T>
              </span>
              <textarea
                data-invoice-edit-notes
                rows={2}
                value={editing.draft.notes}
                onChange={(event) =>
                  setEditing((current) =>
                    current === null
                      ? null
                      : { ...current, draft: { ...current.draft, notes: event.target.value } },
                  )
                }
                placeholder={
                  editing.draft.status === 'void'
                    ? tt('Dôvod zrušenia faktúry')
                    : tt('Nepovinná poznámka na doklade')
                }
                className="w-full rounded-lg border border-border bg-background px-2 py-1 text-xs"
              />
            </label>
            {invoiceEditProblems(editing.draft).map((problem) => (
              <p key={problem} className="text-[11px] text-red-500" data-invoice-edit-problem>
                {problem}
              </p>
            ))}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                data-invoice-edit-cancel
                data-dialog-cancel
                onClick={() => setEditing(null)}
                className="px-3 py-1.5 rounded-lg border border-border text-xs font-bold"
              >
                <T>Zrušiť</T>
              </button>
              <button
                type="button"
                data-invoice-edit-save
                disabled={
                  saveEdit.isPending ||
                  invoiceEditProblems(editing.draft).length > 0 ||
                  invoiceEditBody(editing.draft, invoiceEditDraft(editing.invoice)) === null
                }
                onClick={() => {
                  const body = invoiceEditBody(editing.draft, invoiceEditDraft(editing.invoice))
                  if (body === null) return
                  saveEdit.mutate({ id: editing.invoice.id, data: body })
                }}
                className="px-3 py-1.5 rounded-lg border border-border text-xs font-bold disabled:opacity-40"
              >
                <T>Uložiť</T>
              </button>
            </div>
          </div>
        </div>
      )}

      {asking !== null && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={
            asking.kind === 'run'
              ? tt('Vyfakturovať obdobie')
              : asking.kind === 'raise'
                ? tt('Vystaviť faktúru')
                : tt('Poslať znova')
          }
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          data-billing-confirm={asking.kind}
        >
          <div
            ref={confirmRef}
            onKeyDown={confirmKeyDown}
            className="w-full max-w-md rounded-2xl border border-border bg-card p-4 space-y-3"
          >
            <p className="text-sm" data-billing-confirm-text>
              {asking.kind === 'run'
                ? billingRunConfirmation(runPeriod)
                : asking.kind === 'raise'
                  ? manualInvoiceConfirmation(
                      draft,
                      draftFranchiseName,
                      supplier.defaultVatRatePct,
                    )
                  : resendConfirmation(
                      asking.invoice,
                      billingEmails.get(asking.invoice.franchiseSlug) ?? null,
                    )}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                data-billing-cancel
                data-dialog-cancel
                onClick={() => setAsking(null)}
                className="flex-1 min-h-[36px] rounded-xl border border-border text-xs font-semibold"
              >
                <T>Zrušiť</T>
              </button>
              <button
                type="button"
                data-billing-confirm-go
                disabled={resend.isPending || runBilling.isPending || raise.isPending}
                onClick={() => {
                  if (asking.kind === 'run') {
                    // §3.183 - checked again here rather than trusted from the disabled button, which is
                    // `raise`'s own reasoning three lines down: the year input can be edited behind an
                    // open dialog, and this one raises tax documents for a whole platform.
                    if (billingPeriodProblem(runPeriod.year, runPeriod.month, new Date()) !== null) return
                    runBilling.mutate({ data: { year: runPeriod.year, month: runPeriod.month } })
                    return
                  }
                  if (asking.kind === 'raise') {
                    // Checked again here rather than trusted from the disabled button: the draft can
                    // be edited behind an open dialog.
                    if (manualInvoiceProblem(draft) !== null) return
                    raise.mutate({ data: manualInvoiceBody(draft) })
                    return
                  }
                  // No `to`: the server falls back to the franchise's own billing address, which is
                  // the address the confirmation just named. Sending one from here would let the
                  // screen and the server disagree about who was mailed.
                  resend.mutate({ id: asking.invoice.id, data: {} })
                }}
                className="flex-1 min-h-[36px] rounded-xl border border-primary/40 bg-primary/10 text-primary text-xs font-bold disabled:opacity-40"
              >
                {asking.kind === 'run' ? (
                  <T>Vyfakturovať</T>
                ) : asking.kind === 'raise' ? (
                  <T>Vystaviť</T>
                ) : (
                  <T>Poslať</T>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  )
}
